import React, { useState, useEffect, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Box, Button, Checkbox, FormControlLabel, Typography, Divider, TextField, Alert, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DataObjectIcon from '@mui/icons-material/DataObject';
import PairingQuestion from './PairingQuestion';
import PairingPreview from './PairingPreview';

interface PairingQuestionConfig {
  id: string;
  termA: { text: string; language: string };
  termBOptions: string[];  // Array of correct answers as strings
  termBLanguage: string;  // Single language for all term B options
  hint: string;
}

interface PairingConfigProps {
  initialConfig?: any;
  onConfigChange: (config: any) => void;
}

const PairingConfig: React.FC<PairingConfigProps> = ({ initialConfig = {}, onConfigChange }) => {
  const [questions, setQuestions] = useState<PairingQuestionConfig[]>(initialConfig.questions || []);
  const [isVisual, setIsVisual] = useState(initialConfig.isVisual || false);
  const [jsonInput, setJsonInput] = useState('');
  const [jsonError, setJsonError] = useState('');

  const handleConfigChange = useCallback(() => {
    const config = {
      exerciseType: 'pairing',
      isVisual,
      questions,
    };
    onConfigChange(config);
  }, [isVisual, questions, onConfigChange]);

  useEffect(() => {
    handleConfigChange();
  }, [handleConfigChange]);

  const addQuestion = () => {
    setQuestions([
      ...questions,
      {
        id: uuidv4(),
        termA: { text: '', language: 'en' },
        termBOptions: [''],
        termBLanguage: 'en',
        hint: '',
      },
    ]);
  };

  const updateQuestion = (index: number, updatedQuestion: PairingQuestionConfig) => {
    const updatedQuestions = [...questions];
    updatedQuestions[index] = updatedQuestion;
    setQuestions(updatedQuestions);
  };

  const removeQuestion = (index: number) => {
    setQuestions(questions.filter((_, i) => i !== index));
  };

  const handleJsonImport = () => {
    if (!jsonInput.trim()) {
      setJsonError('Anna JSON-data');
      return;
    }

    try {
      const jsonData = JSON.parse(jsonInput);
      
      // Validate that it's an array
      if (!Array.isArray(jsonData)) {
        setJsonError('JSON pitää olla taulukko (array)');
        return;
      }

      // Convert JSON data to question format and append to existing questions
      const newQuestions = jsonData.map((item: any) => ({
        id: uuidv4(),
        termA: {
          text: item.termA || item.word1 || item.finnish || item.term || '',
          language: item.termALanguage || item.language1 || 'fi'
        },
        termBOptions: Array.isArray(item.termBOptions) 
          ? item.termBOptions 
          : [item.termB || item.word2 || item.english || item.definition || ''],
        termBLanguage: item.termBLanguage || item.language2 || 'en',
        hint: item.hint || item.clue || ''
      }));

      // Append to existing questions
      setQuestions(prev => [...prev, ...newQuestions]);
      setJsonInput(''); // Clear input after successful import
      setJsonError('');
    } catch (error) {
      setJsonError('Virhe JSON:n lukemisessa. Tarkista muoto.');
    }
  };

  const examplePrompt = `Ota kuva sivusta ja lähetä se ChatGPT:lle tämän ohjeen kanssa:

"Analysoi tämä kuva ja luo JSON-taulukko sanaparien harjoitukseen. Etsi suomi-englanti sanaparit tai muut sanapaarit kuvasta.

Muoto:
[
  {
    "termA": "kissa",
    "termALanguage": "fi",
    "termBOptions": ["cat"],
    "termBLanguage": "en",
    "hint": "eläin"
  },
  {
    "termA": "koira", 
    "termALanguage": "fi",
    "termBOptions": ["dog"],
    "termBLanguage": "en",
    "hint": "eläin"
  }
]

Luo vain JSON, ei muuta tekstiä."

Kopioi JSON-vastaus ja liitä se alla olevaan kenttään.`;

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Paritehtävä - Asetukset</Typography>
      <FormControlLabel
        control={
          <Checkbox
            checked={isVisual}
            onChange={() => setIsVisual(!isVisual)}
          />
        }
        label="Visuaalinen tila (vedä ja pudota)"
      />

      <Divider sx={{ my: 2 }} />

      {/* JSON Import Section */}
      <Accordion sx={{ mb: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">
            <DataObjectIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Tuo kysymyksiä JSON:lla
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Käytä ChatGPT:tä luodaksesi JSON-muotoista dataa kuvista:
            </Typography>
            
            <TextField
              label="ChatGPT-ohje (kopioi ja käytä ChatGPT:n kanssa)"
              multiline
              rows={12}
              fullWidth
              value={examplePrompt}
              InputProps={{
                readOnly: true,
              }}
              sx={{ mb: 2, fontFamily: 'monospace' }}
            />

            <TextField
              label="JSON-data (liitä ChatGPT:n vastaus tähän)"
              multiline
              rows={8}
              fullWidth
              value={jsonInput}
              onChange={(e) => {
                setJsonInput(e.target.value);
                setJsonError('');
              }}
              placeholder={`[
  {
    "termA": "kissa",
    "termALanguage": "fi", 
    "termBOptions": ["cat"],
    "termBLanguage": "en",
    "hint": "eläin"
  }
]`}
              sx={{ mb: 2, fontFamily: 'monospace' }}
            />

            <Button
              variant="outlined"
              startIcon={<DataObjectIcon />}
              onClick={handleJsonImport}
              disabled={!jsonInput.trim()}
            >
              Lisää kysymykset JSON:sta
            </Button>

            {jsonError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {jsonError}
              </Alert>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      <Divider sx={{ my: 2 }} />

      {questions.map((question, index) => (
        <PairingQuestion
          key={question.id}
          index={index}
          question={question}
          onQuestionChange={updateQuestion}
          onRemoveQuestion={removeQuestion}
          isVisual={isVisual}
        />
      ))}

      <Box mt={2} mb={2}>
        <Button
          variant="contained"
          startIcon={<AddCircleOutlineIcon />}
          onClick={addQuestion}
        >
          Lisää kysymys
        </Button>
      </Box>

      <Box mt={4}>
        <Typography variant="h6">Esikatselu</Typography>
        <PairingPreview
          config={{
            questions,
            isVisual,
          }}
        />
      </Box>
    </Box>
  );
};

export default PairingConfig;
